import { Button, Col, Row } from "antd";
import Loading from "components/Loading";
import { filter, find, get, some, debounce } from "lodash";
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { bingoImageUrl, clickSoundBingo, loseSoundBingo, selectLoteriaImg } from "utils/config/reference";
import { useAudio } from "utils/context/AudioProvider";
import BingoLoseScreen from "../BingoResultScreens/BingoLoseScreen";
import BingoWinnerScreen from "../BingoResultScreens/BingoWinnerScreen";
import * as Sentry from '@sentry/react';
import {Howl} from "howler";
import { useHowlAudio } from "components/hooks/useHowlProvider";
import SentryWrap from 'components/Sentry';

const LoaderWrapper = styled.div`
    // position:absolute;
    z-index: 100;
`;

const ResponseText = styled.div`
  margin-left: 20px;
`;

const BingoStudentContent = ({
    onStudentAnswer = () => null,
    handleChangeStatus = () => null,
    updateRealTimeClick = () => null,
    bingoResponse,
    submitLoader,
    studentSubmission,
    bingoEndScreen,
    currentUser,
    winnerStatus,
    showSnack
}) => {
    const loseSoundRef = useRef(null);
    const clickSoundRef = useRef(null);
    const [shuffledItems, setShuffledItems] = useState([]);
    const [isAudioReady, setIsAudioReady] = useState(false);
    const [audioUnlocked, setAudioUnlocked] = useState(false);
    const [audioInitialized, setAudioInitialized] = useState(false);
    
    const MatchType = bingoResponse?.configurations?.bingo_type === 'OnlyImage' ? "OnlyImage" : bingoResponse?.bingo_type || "Image";
    const UserSubmissions = find(studentSubmission, (item) => item?.user_id === get(currentUser, "id"));
    const { playHowl, cleanupHowl } = useHowlAudio();

    // Initialize audio with proper iOS handling
    const initializeAudio = useCallback(() => {
        if (!audioInitialized) {
            // Initialize click sound
            if (!clickSoundRef.current) {
                clickSoundRef.current = new Howl({
                    src: [clickSoundBingo],
                    volume: 0.7,
                    html5: false, // Use Web Audio API for better performance
                    preload: true,
                    pool: 3, // Create a pool of sounds for overlapping plays
                    onload: () => {
                        console.log('Click sound loaded successfully');
                        setIsAudioReady(true);
                        // Pre-play and immediately stop to unlock on iOS
                        if (!audioUnlocked) {
                            const id = clickSoundRef.current.play();
                            clickSoundRef.current.stop(id);
                            setAudioUnlocked(true);
                        }
                    },
                    onloaderror: function(id, err) {
                        console.warn('Click sound failed to load:', err);
                        setIsAudioReady(false);
                        // Fallback to HTML5 audio
                        if (clickSoundRef.current) {
                            clickSoundRef.current.unload();
                            clickSoundRef.current = new Howl({
                                src: [clickSoundBingo],
                                volume: 0.7,
                                html5: true,
                                preload: true,
                                onload: () => setIsAudioReady(true)
                            });
                        }
                    },
                    onplayerror: function(id, err) {
                        console.warn('Click sound failed to play:', err);
                    }
                });
            }

            // Initialize lose sound
            if (!loseSoundRef.current) {
                loseSoundRef.current = new Howl({
                    src: [loseSoundBingo],
                    volume: 0.7,
                    html5: false, // Use Web Audio API
                    preload: true,
                    onloaderror: function(id, err) {
                        console.warn('Lose sound failed to load:', err);
                        // Fallback to HTML5
                        if (loseSoundRef.current) {
                            loseSoundRef.current.unload();
                            loseSoundRef.current = new Howl({
                                src: [loseSoundBingo],
                                volume: 0.7,
                                html5: true,
                                preload: true
                            });
                        }
                    }
                });
            }
            
            setAudioInitialized(true);
        }
    }, [audioInitialized, audioUnlocked]);

    // Initialize audio on component mount and user interaction
    useEffect(() => {
        // Initialize immediately
        initializeAudio();

        // Also initialize on first user interaction (for iOS)
        const handleFirstInteraction = () => {
            if (!audioUnlocked) {
                initializeAudio();
                document.removeEventListener('touchstart', handleFirstInteraction);
                document.removeEventListener('click', handleFirstInteraction);
            }
        };

        document.addEventListener('touchstart', handleFirstInteraction, { once: true });
        document.addEventListener('click', handleFirstInteraction, { once: true });

        return () => {
            document.removeEventListener('touchstart', handleFirstInteraction);
            document.removeEventListener('click', handleFirstInteraction);
        };
    }, [initializeAudio, audioUnlocked]);

    // Transform and shuffle items on initial load
    useEffect(() => {
        const UserSubmission = find(studentSubmission, (item) => item?.user_id === get(currentUser, "id"));
        const parsedBingoData = UserSubmission?.answer_text
            ? JSON.parse(UserSubmission.answer_text)
            : [];
        const flatOptions = parsedBingoData.flat();
        setShuffledItems(flatOptions);
    }, [studentSubmission]);

    useEffect(() => {
        const bingoLoseData = filter(studentSubmission, (item) => item?.teacher_response === 'reject');
        const LoseUser = some(bingoLoseData, (item) => item?.user_id === get(currentUser, "id"))
        
        if (LoseUser && !bingoEndScreen && loseSoundRef.current) {
            if (!loseSoundRef.current.playing()) {
                showSnack("Your submission was rejected by the teacher. Please try again!", "info");
                const loseSound = loseSoundRef.current.play();
                if (loseSound === false) {
                    setTimeout(() => {
                        if (loseSoundRef.current) {
                            loseSoundRef.current.play();
                        }
                    }, 50);
                }
                handleChangeStatus(get(currentUser, "id"));
            }
        }
    }, [studentSubmission]);

    const playClickSound = useCallback(() => {
        try {
            if (clickSoundRef.current && isAudioReady) {
                const soundId = clickSoundRef.current.play();
                
                if (soundId === false) {
                    setTimeout(() => {
                        if (clickSoundRef.current) {
                            clickSoundRef.current.play();
                        }
                    }, 50);
                }
            }
        } catch (error) {
            Sentry.captureException(`Error playing click sound: ${error}`);
            console.error("Error playing click sound:", error);
        }
    }, [isAudioReady]);

    const handleCellClick = async (cellIndex) => {
        try {
            if (UserSubmissions?.teacher_response === 'submitted') {
                return showSnack("Please wait for the teacher to review your submission!", "info");
            }
            
            playClickSound();
            
        } catch (error) {
            Sentry.captureException(`628 Error in handleCellClick: ${error}`);
            console.error("Error in handleCellClick:", error);
        }
        
        const updatedItems = Array.isArray(shuffledItems) ? shuffledItems?.map((item, index) =>
            index === cellIndex ? { ...item, selected: !item.selected } : item
        ): [];
        setShuffledItems(updatedItems);
        await updateRealTimeClick(updatedItems);
    };

    const getFinalArray = () => {
        const size = Math.sqrt(shuffledItems.length);
        const groupedArray = [];
        for (let i = 0; i < shuffledItems.length; i += size) {
            groupedArray.push(shuffledItems.slice(i, i + size));
        }
        return groupedArray;
    };

    const handleSubmit = () => {
        const finalResult = getFinalArray();
        if (UserSubmissions?.student_status === 'submitted' || UserSubmissions?.student_status === 'init') {
            showSnack("Your response is successfully submitted!", "success");
        }
        onStudentAnswer(finalResult);
    };

    // Cleanup function
    useEffect(() => {
        return () => {
            if (clickSoundRef.current) {
                clickSoundRef.current.unload();
            }
            if (loseSoundRef.current) {
                loseSoundRef.current.unload();
            }
        };
    }, []);

    if (winnerStatus.has_result) {
        return (<>
            {winnerStatus.is_me_as_winner ? (
                <div className="bingo-win-div">
                    <BingoWinnerScreen />
                </div>
            ) : (
                <div className="bingo-win-div">
                    <BingoLoseScreen />
                </div>
            )}
        </>)
    }
    
    return (
        <SentryWrap>
            {!winnerStatus.has_result && (
                <div className="ideaMdlUpr">
                    <Row>
                        <Col xs={24}>
                            <div className="allDebateCommitSection">
                                <div className="debateMdlTop debateAllTopTxt debateAllTopSpace bingoTopTxt justify-between w-full">
                                    <div className="flex">
                                        <img className="bingo-logo" src={bingoImageUrl} alt="Debate" />
                                        <ResponseText style={{ paddingTop: "0px" }} className="responsetextstyle smallremovemargin">
                                            <span>{get(bingoResponse, "question", "")}</span>
                                        </ResponseText>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col xs={24}>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    width: "100%",
                                }}
                            >
                                <div
                                    style={styles.gridContainer(shuffledItems?.length)}
                                >
                                    {Array.isArray(shuffledItems) && shuffledItems?.map((cell, index) => (
                                        <div
                                            key={index}
                                            style={styles.cell(
                                                MatchType,
                                                cell,
                                                index,
                                                shuffledItems.length
                                            )}
                                            onClick={() => handleCellClick(index)}
                                            className="stuntImgCont relative"
                                            
                                            
                                        >
                                            <div className="cell-with-coin-container">
                                                <div
                                                    style={{
                                                        width: (MatchType === "Image" || MatchType === "OnlyImage") ? "137px" : "100px",
                                                        height: (MatchType === "Image" || MatchType === "OnlyImage") ? "137px" : "100px",
                                                    }}
                                                >
                                                    {cell?.selected && (
                                                        <img
                                                            src={selectLoteriaImg}
                                                            alt="Selected"
                                                            style={{
                                                                width: "55px",
                                                                height: "55px",
                                                                position: "absolute",
                                                                top: "5px",
                                                                left: "5px"
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            {MatchType === "Word" || MatchType === "Letter" ? (
                                                <span className="content" style={{ fontSize: MatchType === "Letter" ? "24px" : "16px" }}>
                                                    {cell.value}
                                                </span>
                                            ) : (MatchType === "Image" || MatchType === "OnlyImage") ? (
                                                <>
                                                    <img
                                                        className="grid-cell-image bingoImgStudent content"
                                                        src={cell?.imageUrl}
                                                        alt=""
                                                        style={{ maxWidth: "100%", maxHeight: "100%", position: "absolute" }}
                                                    />
                                                    {cell.value !== "" && (
                                                        <div className="content" style={{ width: "100%", marginTop: "auto", background: "#0b14426d", color: "white", padding: "4px" }}>
                                                            {cell.value}
                                                        </div>
                                                    )}
                                                </>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Col>
                        <div className="loteriaStdntBtn" style={{position: 'relative'}}>
                            <Button
                                style={{ display: "flex", alignItems: "center" }}
                                className="shareBtnSec debateStudenSubmitButton loteriaStudenSubmitButton"
                                type="primary"
                                disabled={shuffledItems.every((cell) => !cell.selected) || UserSubmissions?.teacher_response === 'submitted'}
                                onClick={handleSubmit}
                            >
                                {submitLoader ? (
                                    <LoaderWrapper>
                                        <Loading size={18} white />
                                    </LoaderWrapper>
                                ) : null}{" "}
                                Lotería
                            </Button>
                        </div>
                    </Row>
                </div>
            )}
        </SentryWrap>
    );
};

const styles = {
    gridContainer: (gridLength) => ({
        display: "grid",
        gridTemplateColumns: `repeat(${Math.sqrt(gridLength)}, 150px)`,
        border: "3px dashed #080808",
        borderRadius: "4px",
        overflow: "auto",
        gap: "0",
    }),
    cell: (MatchType, cell, index, totalItems) => {
        const gridSize = Math.sqrt(totalItems);
        const row = Math.floor(index / gridSize);
        const col = index % gridSize;
        const isLastRow = row === gridSize - 1;
        const isLastColumn = col === gridSize - 1;

        return {
            width: "150px",
            height: "150px",
            display: "flex",
            justifyContent: "center",
            alignItems: (MatchType === "Image" || MatchType === "OnlyImage") ? "flex-start" : "center",
            textAlign: "center",
            overflow: "hidden",
            backgroundColor: cell?.backgroundColor,
            borderRadius: "0",
            border: "none",
            flexDirection: (MatchType === "Image" || MatchType === "OnlyImage") ? "column" : "row",
            gap: (MatchType === "Image" || MatchType === "OnlyImage") ? "10px" : "0",
            borderRight: isLastColumn ? "none" : "3px dashed #080808",
            borderBottom: isLastRow ? "none" : "3px dashed #080808",
            cursor: "pointer",
            color: "#fff",
            // Add touch-friendly styles
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
        };
    },
};

export default BingoStudentContent;